/**
 * Native browser <select> styled to match the design system.
 *
 * @example
 * Always controlled: pass `value` and `onValueChange`.
 *
 * <Select value={val} onValueChange={setVal} placeholder="Pick one">
 *   <SelectItem value="a">Alpha</SelectItem>
 *   <SelectItem value="b">Beta</SelectItem>
 * </Select>
 */

import type { IconName } from './MaterialIcon.js';

import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from './MaterialIcon.js';

function SelectItem({
  value,
  children,
  disabled,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <option value={value} disabled={disabled}>
      {children}
    </option>
  );
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Classes applied to the wrapper. The wrapper sizes to content by default — pass `w-full` to stretch it. */
  wrapperClassName?: string;
  id?: string;
  /** Optional Material icon rendered inside the trigger on the left. */
  leadingIcon?: IconName;
  children?: React.ReactNode;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ value, onValueChange, placeholder, disabled, className, wrapperClassName, id, leadingIcon, children }, ref) => {
    const isPlaceholder = placeholder && value === '';
    return (
      <div className={cn('relative', wrapperClassName)}>
        <select
          ref={ref}
          id={id}
          className={cn(
            'flex h-10 w-full appearance-none items-center rounded-sm border border-input bg-background py-2 pr-8 text-base sm:text-sm ring-offset-background focus:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 truncate',
            leadingIcon ? 'pl-8' : 'pl-2',
            isPlaceholder && 'text-muted-foreground',
            className,
          )}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        >
          {placeholder && (
            <option value="" hidden>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        {leadingIcon && (
          <MaterialIcon
            icon={leadingIcon}
            size={IconSizes.sm}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        )}
        <MaterialIcon
          icon="expand_more"
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 opacity-50"
        />
      </div>
    );
  },
);
Select.displayName = 'Select';

function SelectGroup({ children, label }: { children: React.ReactNode; label?: string }) {
  return <optgroup label={label}>{children}</optgroup>;
}

export { Select, SelectGroup, SelectItem };
